import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,

  LogOut,
  Shield,

  Coins,
  Download,
  DollarSign,
  Building,
  QrCode,
  Trophy,
  Bell,
  Gamepad2,
  Smartphone,
  Receipt,
  BarChart3,
  FileText,
  Settings,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import clsx from 'clsx';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { logout } = useAuthStore();
  const location = useLocation();

  const menuItems = [
    {
      section: 'Overview',
      items: [
        {
          label: 'Dashboard',
          icon: LayoutDashboard,
          path: '/dashboard',
        },
        {
          label: 'Users',
          icon: Users,
          path: '/users',
          badge: 'Live',
        },

      ],
    },


    {
      section: 'Business',
      items: [
        { label: 'Transactions', icon: Receipt, path: '/transactions' },
        { label: 'Analytics', icon: BarChart3, path: '/analytics' },
        { label: 'Reports', icon: FileText, path: '/reports' },
        { label: 'Settings', icon: Settings, path: '/settings' },
        { label: 'Support Inbox', icon: Bell, path: '/support' },
      ],
    },
    {
      section: 'Coin System',
      items: [
        {
          label: 'Purchase Approvals',
          icon: Coins,
          path: '/coins/purchases',
        },
        {
          label: 'Withdrawal Approvals',
          icon: Download,
          path: '/coins/withdrawals',
        },
        {
          label: 'Offer Management',
          icon: DollarSign,
          path: '/coins/offers',
        },
        {
          label: 'Bank Accounts',
          icon: Building,
          path: '/coins/bank-accounts',
        },
        {
          label: 'UPI Accounts',
          icon: QrCode,
          path: '/coins/upi-accounts',
        },
      ],
    },
    {
      section: 'Games',
      items: [
        {
          label: 'Game Status',
          icon: Gamepad2,
          path: '/games/status',
        },
      ],
    },
    {
      section: 'Promotions',
      items: [
        {
          label: 'Lucky Draw',
          icon: Trophy,
          path: '/promotions/lucky-draw',
        },
        {
          label: 'Push Campaigns',
          icon: Bell,
          path: '/promotions/notifications',
        },
      ],
    },
    {
      section: 'Referrals',
      items: [
        {
          label: 'Milestone Tracks',
          icon: Users,
          path: '/referrals/tracks',
        }
      ],
    },
    {
      section: 'System',
      items: [
        {
          label: 'App Updates',
          icon: Smartphone,
          path: '/system/app-versions',
        },
      ],
    },
  ];

  const handleLogout = () => {
    logout();
  };

  const handleNavClick = () => {
    // Auto-close sidebar on mobile when navigating
    onClose?.();
  };

  return (
    <div className="w-[280px] lg:w-64 bg-white text-slate-900 h-full flex flex-col shadow-xl border-r border-slate-200">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900">Rupyo Pay</h1>
              <p className="text-xs text-slate-500 font-medium">Admin Panel</p>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 -mr-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
      </div>



      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 sm:p-4 scrollbar-thin scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-300 overscroll-contain">
        <div className="space-y-5">
          {menuItems.map((section) => (
            <div key={section.section}>
              <h3 className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 px-3">
                {section.section}
              </h3>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path ||
                    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      className={clsx(
                        'flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px]',
                        isActive
                          ? 'bg-purple-50 text-purple-700 shadow-sm ring-1 ring-purple-100'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100'
                      )}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className={clsx("w-5 h-5 flex-shrink-0", isActive ? "text-purple-600" : "text-slate-400 group-hover:text-slate-600")} />
                        <span className="truncate">{item.label}</span>
                      </div>
                      {item.badge && (
                        <span
                          className={clsx(
                            'px-2 py-0.5 text-[10px] rounded-full font-bold uppercase tracking-wide flex-shrink-0',
                            item.badge === 'Live'
                              ? 'bg-green-100 text-green-700 border border-green-200'
                              : item.badge === 'Hot'
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : 'bg-blue-100 text-blue-700 border border-blue-200'
                          )}
                        >
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className="p-3 sm:p-4 border-t border-slate-200 bg-slate-50/50">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 active:bg-red-100 transition-colors duration-200 group min-h-[44px]"
        >
          <LogOut className="w-5 h-5 text-slate-400 group-hover:text-red-500" />
          <span>Sign Out</span>
        </button>

        <div className="mt-3 text-center">
          <p className="text-[10px] text-slate-400 font-medium">
            © 2025 Rupyo Pay Admin
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
